import { useState, useEffect, useContext, ChangeEvent } from 'react';
import { v4 as uuidv4 } from 'uuid';
import update from 'immutability-helper';
import TextField from '@mui/material/TextField';
import Stack from '@mui/material/Stack';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { SelectOption, SelectSubtypes, Disposition } from './types';
import getConditionalControls from '../../Shared/jsonSchemaUtilities/getConditionalControls';
import { WorkflowEngineContext } from '../../WorkflowEngine/workflowEngineReducer';
import ControlState from '../../Shared/controlState';
import JSONSchema7Properties from '../../Shared/jSONSchema7Properties';
import getControlSchemaProperty from '../../Shared/jsonSchemaUtilities/getControlSchemaProperty';
import { OptionOrientation } from '../../Shared/Enums/optionOrientation';
import { GridSize } from '../../Shared/Enums/gridSize';
import OptionOrientationSelect from './OptionOrientationSelect';

export default ({
  dispositions,
  initialOptionOrientation,
  initialOptions,
  isReadOnlyView,
  selectType,
  statusEnabled,
  utilizeHiddenComponents,
  updateOptions,
  updateOptionOrientation,
  renderValidation,
  gridSize
}: {
  dispositions: Disposition[] | null;
  initialOptionOrientation: OptionOrientation;
  initialOptions: SelectOption[];
  isReadOnlyView: boolean;
  selectType: SelectSubtypes;
  statusEnabled: boolean;
  utilizeHiddenComponents: boolean;
  updateOptions: (newOptions: SelectOption[]) => void;
  updateOptionOrientation: (newOptionOreintation: OptionOrientation) => void;
  renderValidation: () => JSX.Element | null;
  gridSize: GridSize;
}) => {
  const { state } = useContext(WorkflowEngineContext);
  const { pages } = state;

  const [selectOptions, setSelectOptions] =
    useState<SelectOption[]>(initialOptions);

  const [optionOrientation, setOptionOrientation] = useState(
    initialOptionOrientation == OptionOrientation.VERTICAL ? '1' : '0'
  );
  const uniqueID = uuidv4();

  const addSelectOption = () => {
    setSelectOptions([
      ...selectOptions,
      { value: 'Value', id: 'Value', status: '', utilize: '' }
    ]);
  };

  const deleteSelectOption = (index: number) => () => {
    setSelectOptions(
      update(selectOptions, {
        $splice: [[index, 1]]
      })
    );
  };

  const changeSelectOptionValue =
    (index: number) => (event: ChangeEvent<HTMLInputElement>) => {
      const changeOption: SelectOption = selectOptions[index];
      changeOption.value = event.target.value;

      setSelectOptions(
        update(selectOptions, {
          $splice: [[index, 1, changeOption]]
        })
      );
      changeSelectOptionId(index);
    };
  const changeSelectOptionId = (index: number) => {
    const changeOption: SelectOption = selectOptions[index];

    changeOption.id = changeOption.value.replace(/\s/g, '').replace(/\./g, '_');

    setSelectOptions(
      update(selectOptions, {
        $splice: [[index, 1, changeOption]]
      })
    );
  };

  const changeSelectStatus = (index: number) => (event: SelectChangeEvent) => {
    const changeOption: SelectOption = selectOptions[index];
    changeOption.status = event.target.value;
    setSelectOptions(
      update(selectOptions, {
        $splice: [[index, 1, changeOption]]
      })
    );
  };

  const handleOptionOrientationChange = (event: SelectChangeEvent) => {
    setOptionOrientation(event.target.value);
  };

  const handleShowComponentSelectChange =
    (index: number) => (event: SelectChangeEvent<string[]>) => {
      const changeOption: SelectOption = selectOptions[index];
      changeOption.utilize = (event.target.value as string[])
        .filter((v) => v !== '')
        .join('|');
      setSelectOptions(
        update(selectOptions, {
          $splice: [[index, 1, changeOption]]
        })
      );
    };

  const renderSingleChoice = () => {
    return (
      <>
        <Stack direction="row" spacing={3}>
          <Stack sx={{ flexGrow: 4 }}>
            <TextField
              fullWidth
              id={`${uniqueID}-${0}-OptionValue`}
              label="Option Value"
              size="small"
              value={selectOptions[0].value}
              onChange={changeSelectOptionValue(0)}
              disabled={isReadOnlyView}
            />
          </Stack>
          <Stack sx={{ flexGrow: 4 }}>
            <TextField
              fullWidth
              id={`${uniqueID}-${0}-OptionID`}
              label="Option ID"
              size="small"
              value={selectOptions[0].id}
              disabled={true}
            />
          </Stack>
          {statusEnabled && renderStatusSelect(selectOptions[0].status, 0)}
          {utilizeHiddenComponents &&
            renderHiddenComponents(selectOptions[0].utilize?.split('|'), 0)}
        </Stack>
        {renderValidation()}
      </>
    );
  };

  const renderMultipleChoiceWithStatus = () => {
    return (
      <>
        {selectType === SelectSubtypes.MULTI_CHECKBOXES ||
        selectType === SelectSubtypes.RADIO_BUTTONS ? (
          <OptionOrientationSelect
            isReadOnlyView={isReadOnlyView}
            optionOrientation={optionOrientation}
            handleOptionOrientationChange={handleOptionOrientationChange}
            gridSize={gridSize}
          />
        ) : null}
        {selectOptions.map((option, index) => {
          return (
            <Stack direction="row" spacing={3} key={`selectOption-${index}`}>
              <Stack sx={{ flexGrow: 3 }}>
                <TextField
                  fullWidth
                  id={`${uniqueID}-${index}-OptionValue`}
                  label="Option Value"
                  size="small"
                  value={`${option.value}`}
                  onChange={changeSelectOptionValue(index)}
                  disabled={isReadOnlyView}
                />
              </Stack>
              <Stack sx={{ flexGrow: 3 }}>
                <TextField
                  fullWidth
                  id={`${uniqueID}-${index}-OptionID`}
                  label="Option ID"
                  size="small"
                  value={option.id}
                  disabled={true}
                />
              </Stack>
              {statusEnabled && renderStatusSelect(option.status, index)}
              {utilizeHiddenComponents &&
                renderHiddenComponents(option.utilize?.split('|'), index)}
              <Stack
                sx={{
                  flexGrow: 1,
                  display: 'flex',
                  alignItems: 'flex-start'
                }}
              >
                <IconButton
                  sx={{ padding: '3px' }}
                  onClick={deleteSelectOption(index)}
                  disabled={isReadOnlyView}
                >
                  <DeleteIcon />
                </IconButton>
              </Stack>
            </Stack>
          );
        })}
        {renderValidation()}
        <Stack>
          <Button
            onClick={addSelectOption}
            style={{
              fontSize: '14px',
              display: 'flex',
              justifyContent: 'flex-start',
              maxWidth: '180px'
            }}
            startIcon={<AddIcon />}
            disabled={isReadOnlyView}
          >
            Add another option
          </Button>
        </Stack>
      </>
    );
  };

  const renderStatusSelect = (value: string, index: number) => {
    return dispositions === null ? null : (
      <Stack sx={{ flexGrow: 3, width: '200px' }}>
        <FormControl disabled={isReadOnlyView} size="small">
          <InputLabel>Status</InputLabel>
          <Select
            label="Status"
            value={value}
            onChange={changeSelectStatus(index)}
          >
            <MenuItem value={''}>
              {dispositions.length > 0
                ? 'Select a status'
                : 'No status available'}
            </MenuItem>
            {dispositions &&
              dispositions.map((value) => {
                return (
                  <MenuItem
                    value={value.dispositionCode}
                    key={`Status-${value.dispositionCode}`}
                  >
                    {value.description}
                  </MenuItem>
                );
              })}
          </Select>
        </FormControl>
      </Stack>
    );
  };

  const renderHiddenComponents = (value: string[], index: number) => {
    // get non status-enabled conditional controls
    const conditionalControls = getConditionalControls(pages).filter(
      (control) => {
        return !getControlSchemaProperty(control, 'statusEnabled') as boolean;
      }
    );

    return (
      <Stack sx={{ flexGrow: 3, width: '200px' }}>
        <FormControl disabled={isReadOnlyView} size="small">
          <InputLabel>Show</InputLabel>
          <Select
            label="Show"
            multiple
            onChange={handleShowComponentSelectChange(index)}
            value={value}
          >
            <MenuItem value={''}>
              {conditionalControls?.length > 0
                ? 'Select component(s)'
                : 'No component(s) available'}
            </MenuItem>
            {conditionalControls?.map((ctrl: ControlState) => {
              const ctrlProperties = ctrl.schema
                ?.properties as JSONSchema7Properties;
              const ctrlId = ctrlProperties['id'].default as string;

              return (
                <MenuItem value={ctrlId} key={`Conditional-${ctrlId}`}>
                  {ctrlId}
                </MenuItem>
              );
            })}
          </Select>
        </FormControl>
      </Stack>
    );
  };

  useEffect(() => {
    const initialOptions =
      selectType === SelectSubtypes.SINGLE_CHECKBOX
        ? [selectOptions[0]]
        : selectOptions;

    updateOptions(initialOptions);
  }, [selectOptions]);

  useEffect(() => {
    updateOptionOrientation(
      optionOrientation == '1'
        ? OptionOrientation.VERTICAL
        : OptionOrientation.HORIZONTAL
    );
  }, [optionOrientation]);

  return (
    <Stack direction="column" spacing={3}>
      {selectType === SelectSubtypes.SINGLE_CHECKBOX
        ? renderSingleChoice()
        : renderMultipleChoiceWithStatus()}
    </Stack>
  );
};
